import { DOMElement } from '../utils/DOMElement';
import { WordingService } from '../services/WordingService';
import { Identity } from '../components/Identity';

/**
 * Password-related Component.
 */
export class PasswordError {
  /**
   * Cnil lower case message dom selector.
   *
   * @type {string}
   */
  static cnilLowerCaseDOMSelector = '#cnil-lowercase';

  /**
   * Cnil min chars message dom selector.
   *
   * @type {string}
   */
  static cnilMinCharsDOMSelector = '#cnil-min-chars';

  /**
   * Cnil numbers message dom selector.
   *
   * @type {string}
   */
  static cnilNumbersDOMSelector = '#cnil-numbers';

  /**
   * Cnil upper case message dom selector.
   *
   * @type {string}
   */
  static cnilUpperCaseDOMSelector = '#cnil-uppercase';

  /**
   * Password error container dom selector.
   *
   * @type {string}
   */
  static containerDOMSelector = '#password-security-checks';

  /**
   * Main password error dom selector.
   *
   * @type {string}
   */
  static mainErrorDOMSelector = '#password-error';

  /**
   * Main password error dom selector.
   *
   * @type {string}
   */
  static mainErrorTextDOMSelector = '#password-error-text';

  /**
   * Constructor.
   */
  constructor() {
    this.isInError = false;
    this.isPristine = true;
    this.containerDOM = document.querySelector(PasswordError.containerDOMSelector);
    this.cnilMinCharsDOM = document.querySelector(PasswordError.cnilMinCharsDOMSelector);
    this.cnilLowerCaseDOM = document.querySelector(PasswordError.cnilLowerCaseDOMSelector);
    this.cnilUpperCaseDOM = document.querySelector(PasswordError.cnilUpperCaseDOMSelector);
    this.cnilNumbersDOM = document.querySelector(PasswordError.cnilNumbersDOMSelector);
    this.cnilStatusDOMs = {
      min: this.cnilMinCharsDOM,
      uppercase: this.cnilUpperCaseDOM,
      lowercase: this.cnilLowerCaseDOM,
      digits: this.cnilNumbersDOM,
    };
    this.mainErrorStatusKeys = ['space', 'specialChars', 'accentedChars'];
    this.mainErrorWordings = {
      accentedChars: WordingService.getWording('pwdErrorAccentMessage'),
      filled: WordingService.getWording('pwdErrorEmptyMessage'),
      max: WordingService.getWording('pwdErrorMaxCharsMessage'),
      space: WordingService.getWording('pwdErrorSpaceMessage'),
      sameAsEmail: WordingService.getWording('pwdErrorSameAsEmail'),
      specialChars: WordingService.getWording('pwdErrorSpecialCharMessage'),
    };
    this.mainErrorDOM = document.querySelector(PasswordError.mainErrorDOMSelector);
    this.mainErrorTextDOM = document.querySelector(PasswordError.mainErrorTextDOMSelector);
    this.passwordDOM = document.querySelector(Identity.passwordDOMSelector);
  }

  /**
   * Update all password error related fields and values.
   *
   * @param {obj} passwordStatuses - All statuses of the password.
   * @param {bool} isOnBlur - Whether the update is triggered by an onblur event.
   */
  update(passwordStatuses, isOnBlur) {
    if (!passwordStatuses.result) {
      this.isInError = true;
    } else {
      this.isInError = false;
    }

    this.updateMainMessage(passwordStatuses, isOnBlur);
    this.updateCNILChecks(passwordStatuses);
  }

  /**
   * Update Main error field.
   *
   * @param {obj} passwordStatuses - All statuses of the password.
   * @param {bool} doCheckEmpty - All statuses of the password.
   */
  updateMainMessage(passwordStatuses, doCheckEmpty) {
    const firstErrorKey = this.getMainErrorKey(passwordStatuses, doCheckEmpty);

    if (firstErrorKey !== '') {
      this.mainErrorTextDOM.innerHTML = this.mainErrorWordings[firstErrorKey];
      this.mainErrorDOM.setAttribute('aria-label', this.mainErrorWordings[firstErrorKey]);
      DOMElement.removeClass('eui-error-hide', this.mainErrorDOM);
      DOMElement.removeClass('pristine', this.mainErrorDOM);
      DOMElement.addClass('eui-has-error', this.passwordDOM);
      DOMElement.addClass('is-invalid', this.passwordDOM);
    } else {
      this.mainErrorDOM.removeAttribute('aria-label');
      DOMElement.addClass('eui-error-hide', this.mainErrorDOM);
      DOMElement.removeClass('eui-has-error', this.passwordDOM);
      DOMElement.removeClass('is-invalid', this.passwordDOM);
    }

    if (this.isInError) {
      this.passwordDOM.setAttribute('aria-invalid', 'true');
    } else {
      this.passwordDOM.setAttribute('aria-invalid', 'false');
    }
  }

  /**
   * Show the password error stating that it is the same as the email.
   */
  setSameAsLoginError() {
    this.isInError = true;
    this.mainErrorTextDOM.innerHTML = this.mainErrorWordings.sameAsEmail;
    DOMElement.removeClass('eui-error-hide', this.mainErrorDOM);
    DOMElement.removeClass('pristine', this.mainErrorDOM);
    DOMElement.addClass('eui-has-error', this.passwordDOM);
    DOMElement.addClass('is-invalid', this.passwordDOM);
    this.passwordDOM.setAttribute('aria-invalid', 'true');
  }

  /**
   * Update CNIL errors (set css class).
   *
   * @param {obj} passwordStatuses - All statuses of the password.
   */
  updateCNILChecks(passwordStatuses) {
    for (const cnilStatusKey in this.cnilStatusDOMs) {
      if (cnilStatusKey && this.cnilStatusDOMs[cnilStatusKey]) {
        if (passwordStatuses[cnilStatusKey]) {
          this.cnilStatusDOMs[cnilStatusKey].className = 'ok';
          this.cnilStatusDOMs[cnilStatusKey].setAttribute('aria-hidden', 'true');
        } else {
          this.cnilStatusDOMs[cnilStatusKey].className = 'ko';
          this.cnilStatusDOMs[cnilStatusKey].setAttribute('aria-hidden', 'false');
        }
      }
    }
  }

  /**
   * Returns the first key with error status found in password (based on its position in password).
   *
   * @param  {obj} passwordStatuses - All statuses of the password.
   * @param {bool} doCheckEmpty - Check whether password is empty (only performed on blur event).
   * @returns {string} The first key with error status found in password.
   */
  getMainErrorKey(passwordStatuses, doCheckEmpty) {
    // show only max error, no matter if other errors are found
    if (passwordStatuses.max === false) {
      return 'max';
    }
    // show empty error message when exiting the field
    if (doCheckEmpty === true && passwordStatuses.filled === false) {
      return 'filled';
    }

    // filter keys in error (to avoid unnecessary tests later on)
    const keysInError = this.mainErrorStatusKeys.filter((key) => {
      if (passwordStatuses[key] !== true) {
        return key;
      }
    });

    if (keysInError.length === 0) {
      return '';
    }

    // get the key with the lowest position (hence the first error from left to right)
    let lowestErrorPosition = passwordStatuses[keysInError[0]];
    let firstErrorKey = '';

    keysInError.map((key) => {
      if (passwordStatuses[key] <= lowestErrorPosition) {
        lowestErrorPosition = passwordStatuses[key];
        firstErrorKey = key;
      }
    });

    return firstErrorKey;
  }

  /**
   * Hide password messages
   * - only if password is empty.
   *
   * @param {bool} isPasswordEmpty - True if the password is empty, false otherwise.
   */
  hideMessages(isPasswordEmpty) {
    if (isPasswordEmpty) {
      DOMElement.addClass('eui-hide-partially', this.containerDOM);
    }
  }

  /**
   * Show password messages
   * - show it all the first time
   * - only show it partially the rest of the time.
   */
  showMessages() {
    if (this.isPristine) {
      DOMElement.removeClass('pristine', this.containerDOM);
      this.isPristine = false;
    } else {
      DOMElement.removeClass('eui-hide-partially', this.containerDOM);
    }
  }
}
